import { 
  Component, 
  EventEmitter, 
  Input, 
  Output, 
  ViewChild, 
  ElementRef, 
  HostListener, 
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  TemplateRef,
  ContentChild,
  OnChanges,
  AfterViewInit,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NiIconComponent } from '../ni-icon/ni-icon.component';
import { animate, state, style, transition, trigger } from '@angular/animations';

// Types
export interface FontSizeType {
  [key: number]: string;
}

export interface FontWeightType {
  [key: number]: string;
}

export interface SizeType {
  [key: string]: string;
}

export interface PaddingType {
  [key: string]: string;
}

export interface DropdownOption {
  label: string;
  value: any;
}

export interface ButtonVariant {
  button: string;
  hover?: string;
}

// Style constants
export class ButtonStyleConstants {
  static readonly SIZES: SizeType = {};

  static readonly RADII = {};

  static readonly COLORS = {
    primary: {
      base: 'bg-primary-500 text-white',
      hover: 'hover:bg-primary-600',
      active: 'active:bg-primary-700',
      border: 'border-primary-500'
    },
    secondary: {
      base: 'bg-secondary-500 text-white',
      hover: 'hover:bg-secondary-600',
      active: 'active:bg-secondary-700',
      border: 'border-secondary-500'
    },
    error: {
      base: 'bg-danger-500 text-white',
      hover: 'hover:bg-danger-600',
      active: 'active:bg-danger-700',
      border: 'border-danger-500'
    },
    success: {
      base: 'bg-success-500 text-white',
      hover: 'hover:bg-success-600',
      active: 'active:bg-success-700',
      border: 'border-success-500'
    },
    warning: {
      base: 'bg-warning-500 text-white',
      hover: 'hover:bg-warning-600',
      active: 'active:bg-warning-700',
      border: 'border-warning-500'
    },
    basic: {
      base: 'bg-gray-100 text-gray-800',
      hover: 'hover:bg-gray-200',
      active: 'active:bg-gray-300',
      border: 'border-gray-300'
    },
    link: {
      base: 'text-primary-500 bg-transparent',
      hover: 'hover:text-primary-600 hover:underline',
      active: 'active:text-primary-700',
      border: 'border-transparent'
    },
    text: {
      base: 'text-gray-800 bg-transparent',
      hover: 'hover:bg-gray-100',
      active: 'active:bg-gray-200',
      border: 'border-transparent'
    }
  };

  static readonly VARIANTS = {
    contained: {
      base: 'shadow-sm bg-primary-500 text-white',
      hover: 'hover:shadow-md',
      active: 'active:shadow-none'
    },
    outlined: {
      base: 'border bg-transparent text-primary-500',
      hover: 'hover:bg-opacity-10',
      active: 'active:bg-opacity-20'
    },
    text: {
      base: 'text-primary-500 bg-transparent',
      hover: '',
      active: ''
    },
    raised: {
      base: 'shadow-md bg-primary-500 text-white',
      hover: 'hover:shadow-lg',
      active: 'active:shadow-sm'
    },
    plain: {
      base: 'bg-transparent',
      hover: '',
      active: ''
    },
    link: {
      base: 'underline text-primary-500 bg-transparent',
      hover: '',
      active: ''
    }
  };
}

@Component({
  selector: 'NiButton',
  templateUrl: './ni-button.component.html',
  styleUrls: ['./ni-button.component.css'],
  standalone: true,
  imports: [CommonModule, NiIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('buttonPress', [
      state('pressed', style({
        transform: 'scale(0.95)',
        'transform-origin': 'center'
      })),
      state('released', style({
        transform: 'scale(1)',
        'transform-origin': 'center'
      })),
      transition('released => pressed', [
        animate('100ms ease-out')
      ]),
      transition('pressed => released', [
        animate('150ms ease-in')
      ])
    ]),
    trigger('contentFade', [
      state('in', style({
        opacity: 1,
        transform: 'translateY(0)'
      })),
      state('out', style({
        opacity: 0,
        transform: 'translateY(-2px)'
      })),
      transition('out => in', [
        animate('200ms ease-out')
      ]),
      transition('in => out', [
        animate('150ms ease-in')
      ])
    ])
  ]
})
export class NiButtonComponent implements OnChanges, AfterViewInit {
  // Constants
  sizes = ButtonStyleConstants.SIZES;
  radii = ButtonStyleConstants.RADII;
  variants = ButtonStyleConstants.VARIANTS;
  colors = ButtonStyleConstants.COLORS;

  // Button Configuration Inputs
  @Input() color: keyof typeof ButtonStyleConstants.COLORS = 'primary';
  @Input() variant: keyof typeof ButtonStyleConstants.VARIANTS = 'contained';
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() paddingSize: keyof PaddingType = '';
  @Input() size: keyof SizeType = '';
  @Input() radius: string = '';
  @Input() fullWidth: boolean = false;
  
  // Content Inputs
  @Input() label: string = '';
  @Input() text: string = ''; // Can be used for label (legacy) or text color
  @Input() leadingIcon: string = '';
  @Input() trailingIcon: string = '';
  @Input() leadingIconClass: string = '';
  @Input() trailingIconClass: string = '';

  // Padding props
  @Input() px?: string;
  @Input() py?: string;
  @Input() p?: string;

  // Color props
  @Input() bg?: string;
  @Input() textColor?: string; // Explicit text color prop

  // Border props
  @Input() border?: string;
  @Input() borderColor?: string;
  
  // Directional border props
  @Input() borderB?: string; // border-b (bottom)
  @Input() borderT?: string; // border-t (top)
  @Input() borderL?: string; // border-l (left)
  @Input() borderR?: string; // border-r (right)
  
  // Directional border color props
  @Input() borderBColor?: string; // border-b color
  @Input() borderTColor?: string; // border-t color
  @Input() borderLColor?: string; // border-l color
  @Input() borderRColor?: string; // border-r color

  // Radius prop
  @Input() rounded?: string;

  // Hover props
  @Input() hoverBg?: string;
  @Input() hoverText?: string;
  @Input() hoverBorder?: string;

  // Shadow props
  @Input() shadow?: string;
  @Input() hoverShadow?: string;

  // Ring props
  @Input() ring?: string;
  @Input() hoverRing?: string;

  // Size props
  @Input() h?: string;
  @Input() w?: string;

  // Font props
  @Input() fontSize?: string;
  @Input() fontWeight?: string;
  @Input() underline?: boolean;
  @Input() hoverUnderline?: boolean;

  // Style Inputs (legacy)
  @Input() fontSizeLegacy: keyof FontSizeType = 16;
  @Input() fontWeightLegacy: keyof FontWeightType = 600;
  @Input() isRounded: boolean = false;

  // Behavior Inputs
  @Input() disabled: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() isFocused: boolean = false;
  @Input() loading: boolean = false;
  @Input() loadingText?: string;
  @Input() loaderSize: string = 'w-4 h-4';
  @Input() loaderColor: string = 'text-white';
  @Input() preserveWidthDuringLoading: boolean = true;
  @Input() isFullWidth: boolean = false;

  // Accessibility
  @Input() ariaLabel?: string;

  // Legacy Inputs
  @Input() icon: string = '';
  @Input() iconPos: 'left' | 'right' = 'left';
  @Input() badge: string = '';
  @Input() badgeClass: string = '';
  @Input() batchColur: string = 'primary';
  @Input() loadingIcon: string = '';
  @Input() autofocus: boolean = false;
  @Input() tabindex: number = 0;
  @Input() buttonProps: any = {};
  @Input() styleClass: string = '';
  @Input() style: { [klass: string]: any } = {};

  // Outputs
  @Output() onClick = new EventEmitter<MouseEvent>();
  @Output() onLeadingIconClick = new EventEmitter<MouseEvent>();
  @Output() onTrailingIconClick = new EventEmitter<MouseEvent>();
  @Output() onFocus = new EventEmitter<FocusEvent>();
  @Output() onBlur = new EventEmitter<FocusEvent>();

  @ViewChild('buttonElement') buttonElement!: ElementRef<HTMLButtonElement>;

  // animation state
  buttonState: 'pressed' | 'released' = 'released';
  hoverState: 'hover' | 'noHover' = 'noHover';
  isAnimating: boolean = false;

  // Ripple state
  ripple: { x: number; y: number; size: number; key: number } | null = null;

  // ContentChild for custom icon and loadingIcon
  @ContentChild('icon', { read: TemplateRef }) iconTemplate?: TemplateRef<any>;
  @ContentChild('loadingIcon', { read: TemplateRef }) loadingIconTemplate?: TemplateRef<any>;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly cdr: ChangeDetectorRef
  ) {}

  get effectiveLabel(): string {
    // If text is a color value (common color names/patterns), it's for color, not label
    if (this.isTextColorValue(this.text)) {
      return this.label;
    }
    return this.label ?? this.text;
  }

  get effectiveTextColor(): string | undefined {
    // Priority: textColor > text (if text is a color value)
    if (this.textColor) {
      return this.textColor;
    }
    if (this.isTextColorValue(this.text)) {
      return this.text;
    }
    return undefined;
  }

  private isTextColorValue(value: string): boolean {
    if (!value) return false;
    
    // Check exact string matches
    if (value === 'white' || value === 'black' || value === 'transparent') {
      return true;
    }
    
    // Check Tailwind color pattern - split into smaller patterns to reduce complexity
    const tailwindColors1 = /^(slate|gray|zinc|neutral|stone|red|orange|amber)-\d+$/;
    const tailwindColors2 = /^(yellow|lime|green|emerald|teal|cyan|sky)-\d+$/;
    const tailwindColors3 = /^(blue|indigo|violet|purple|fuchsia|pink|rose)-\d+$/;
    if (tailwindColors1.exec(value) || tailwindColors2.exec(value) || tailwindColors3.exec(value)) {
      return true;
    }
    
    // Check hex color
    const hexPattern = /^#[0-9a-fA-F]{3,6}$/;
    if (hexPattern.exec(value)) {
      return true;
    }
    
    // Check CSS color functions
    return value.startsWith('rgb(') || 
           value.startsWith('rgba(') || 
           value.startsWith('hsl(') || 
           value.startsWith('hsla(');
  }

  private isArbitraryValue(value: string): boolean {
    if (!value) return false;
    // Arbitrary values in Tailwind are wrapped in square brackets, e.g., "[12px]", "[24px]"
    return value.startsWith('[') && value.endsWith(']');
  }

  private extractArbitraryValue(value: string): string {
    // Extract the value from brackets, e.g., "[12px]" -> "12px"
    if (this.isArbitraryValue(value)) {
      return value.slice(1, -1);
    }
    return value;
  }

  get buttonStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    
    this.applyPaddingStyles(styles);
    this.applySizeStyles(styles);
    this.applyBorderRadiusStyles(styles);
    this.applyBorderColorStyles(styles);
    this.applyDirectionalBorderStyles(styles);
    this.applyColorStyles(styles);
    
    // Merge with any existing inline styles
    return { ...this.style, ...styles };
  }

  private applyPaddingStyles(styles: { [key: string]: string }): void {
    if (this.p && this.isArbitraryValue(this.p)) {
      styles['padding'] = this.extractArbitraryValue(this.p);
    }
    
    if (this.px && this.isArbitraryValue(this.px)) {
      const value = this.extractArbitraryValue(this.px);
      styles['padding-left'] = value;
      styles['padding-right'] = value;
    }
    
    if (this.py && this.isArbitraryValue(this.py)) {
      const value = this.extractArbitraryValue(this.py);
      styles['padding-top'] = value;
      styles['padding-bottom'] = value;
    }
  }

  private applySizeStyles(styles: { [key: string]: string }): void {
    if (this.h && this.isArbitraryValue(this.h)) {
      styles['height'] = this.extractArbitraryValue(this.h);
    }
    
    if (this.w && this.isArbitraryValue(this.w)) {
      styles['width'] = this.extractArbitraryValue(this.w);
    }
  }

  private applyBorderRadiusStyles(styles: { [key: string]: string }): void {
    if (this.rounded && this.isArbitraryValue(this.rounded)) {
      styles['border-radius'] = this.extractArbitraryValue(this.rounded);
    }
  }

  private applyBorderColorStyles(styles: { [key: string]: string }): void {
    if (this.borderColor && this.isHexColor(this.borderColor)) {
      styles['border-color'] = this.cleanColorValue(this.borderColor);
    }
  }

  private applyDirectionalBorderStyles(styles: { [key: string]: string }): void {
    this.applyDirectionalBorderColor(styles);
    this.applyDirectionalBorderWidth(styles);
  }

  private applyDirectionalBorderColor(styles: { [key: string]: string }): void {
    if (this.borderBColor && this.isHexColor(this.borderBColor)) {
      styles['border-bottom-color'] = this.cleanColorValue(this.borderBColor);
    }
    
    if (this.borderTColor && this.isHexColor(this.borderTColor)) {
      styles['border-top-color'] = this.cleanColorValue(this.borderTColor);
    }
    
    if (this.borderLColor && this.isHexColor(this.borderLColor)) {
      styles['border-left-color'] = this.cleanColorValue(this.borderLColor);
    }
    
    if (this.borderRColor && this.isHexColor(this.borderRColor)) {
      styles['border-right-color'] = this.cleanColorValue(this.borderRColor);
    }
  }

  private applyDirectionalBorderWidth(styles: { [key: string]: string }): void {
    if (this.borderB && this.isArbitraryValue(this.borderB)) {
      styles['border-bottom-width'] = this.extractArbitraryValue(this.borderB);
    }
    
    if (this.borderT && this.isArbitraryValue(this.borderT)) {
      styles['border-top-width'] = this.extractArbitraryValue(this.borderT);
    }
    
    if (this.borderL && this.isArbitraryValue(this.borderL)) {
      styles['border-left-width'] = this.extractArbitraryValue(this.borderL);
    }
    
    if (this.borderR && this.isArbitraryValue(this.borderR)) {
      styles['border-right-width'] = this.extractArbitraryValue(this.borderR);
    }
  }

  private applyColorStyles(styles: { [key: string]: string }): void {
    if (this.bg && this.isHexColor(this.bg)) {
      styles['background-color'] = this.cleanColorValue(this.bg);
    }
    
    const textColorValue = this.effectiveTextColor;
    if (textColorValue && this.isHexColor(textColorValue)) {
      styles['color'] = this.cleanColorValue(textColorValue);
    }
  }

  private cleanColorValue(color: string): string {
    return color.startsWith('[') && color.endsWith(']') 
      ? color.slice(1, -1) 
      : color;
  }
  
  private isHexColor(value: string): boolean {
    if (!value) return false;
    // Check if it's a hex color (starts with # and is 3, 4, 6, or 8 characters after #)
    // Also handle values wrapped in brackets like [#E0F2FE]
    const cleanValue = value.startsWith('[') && value.endsWith(']') ? value.slice(1, -1) : value;
    const hexPattern = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
    return hexPattern.exec(cleanValue) !== null;
  }

  get isButtonDisabled(): boolean {
    return this.disabled || this.isDisabled;
  }

  get showLoader(): boolean {
    return this.loading && !this.isButtonDisabled;
  }

  get showContent(): boolean {
    return !this.loading || this.isButtonDisabled;
  }

  get buttonClasses(): string {
    const classes: string[] = [];
    
    this.applyPaddingClasses(classes);
    this.applyColorClasses(classes);
    this.applyBorderClasses(classes);
    this.applyDirectionalBorderClasses(classes);
    this.applyDirectionalBorderColorClasses(classes);
    this.applyRadiusClasses(classes);
    this.applyHoverClasses(classes);
    this.applyShadowClasses(classes);
    this.applyRingClasses(classes);
    this.applySizeClasses(classes);
    this.applyFontClasses(classes);
    this.applyUnderlineClasses(classes);
    this.applyLegacyClasses(classes);
    
    return classes.filter(Boolean).join(' ');
  }

  private applyPaddingClasses(classes: string[]): void {
    if (this.p && !this.isArbitraryValue(this.p)) {
      classes.push(`p-${this.p}`);
    }
    
    if (this.px && !this.isArbitraryValue(this.px)) {
      classes.push(`px-${this.px}`);
    }
    
    if (this.py && !this.isArbitraryValue(this.py)) {
      classes.push(`py-${this.py}`);
    }
  }

  private applyColorClasses(classes: string[]): void {
    if (this.bg && !this.isHexColor(this.bg)) {
      classes.push(`bg-${this.bg}`);
    }
    
    const textColorValue = this.effectiveTextColor;
    if (textColorValue && !this.isHexColor(textColorValue)) {
      classes.push(`text-${textColorValue}`);
    }
  }

  private applyBorderClasses(classes: string[]): void {
    const borderWidth = this.border ?? (this.borderColor ? '1' : undefined);
    
    if (!borderWidth) {
      return;
    }
    
    if (borderWidth === '1') {
      classes.push('border');
    } else if (borderWidth.startsWith('[') && borderWidth.endsWith(']')) {
      classes.push(`border-${borderWidth}`);
    } else {
      classes.push(`border-${borderWidth}`);
    }
    
    if (this.borderColor && !this.isHexColor(this.borderColor)) {
      classes.push(`border-${this.borderColor}`);
    }
  }

  private applyDirectionalBorderClasses(classes: string[]): void {
    this.addDirectionalBorderClass(classes, this.borderB, 'border-b');
    this.addDirectionalBorderClass(classes, this.borderT, 'border-t');
    this.addDirectionalBorderClass(classes, this.borderL, 'border-l');
    this.addDirectionalBorderClass(classes, this.borderR, 'border-r');
  }

  private addDirectionalBorderClass(classes: string[], value: string | undefined, prefix: string): void {
    if (!value || this.isArbitraryValue(value)) {
      return;
    }
    
    if (value === '1') {
      classes.push(prefix);
    } else {
      classes.push(`${prefix}-${value}`);
    }
  }

  private applyDirectionalBorderColorClasses(classes: string[]): void {
    if (this.borderBColor && !this.isHexColor(this.borderBColor)) {
      classes.push(`border-b-${this.borderBColor}`);
    }
    
    if (this.borderTColor && !this.isHexColor(this.borderTColor)) {
      classes.push(`border-t-${this.borderTColor}`);
    }
    
    if (this.borderLColor && !this.isHexColor(this.borderLColor)) {
      classes.push(`border-l-${this.borderLColor}`);
    }
    
    if (this.borderRColor && !this.isHexColor(this.borderRColor)) {
      classes.push(`border-r-${this.borderRColor}`);
    }
  }

  private applyRadiusClasses(classes: string[]): void {
    if (this.rounded && !this.isArbitraryValue(this.rounded)) {
      classes.push(`rounded-${this.rounded}`);
    }
  }

  private applyHoverClasses(classes: string[]): void {
    if (this.hoverBg) {
      classes.push(`hover:bg-${this.hoverBg}`);
    }
    
    if (this.hoverText) {
      classes.push(`hover:text-${this.hoverText}`);
    }
    
    if (this.hoverBorder) {
      classes.push(`hover:border-${this.hoverBorder}`);
    }
  }

  private applyShadowClasses(classes: string[]): void {
    if (this.shadow) {
      classes.push(`shadow-${this.shadow}`);
    }
    
    if (this.hoverShadow) {
      classes.push(`hover:shadow-${this.hoverShadow}`);
    }
  }

  private applyRingClasses(classes: string[]): void {
    if (this.ring) {
      classes.push(`ring-${this.ring}`);
    }
    
    if (this.hoverRing) {
      classes.push(`hover:ring-${this.hoverRing}`);
    }
  }

  private applySizeClasses(classes: string[]): void {
    if (this.h && !this.isArbitraryValue(this.h)) {
      classes.push(`h-${this.h}`);
    }
    
    if (this.w && !this.isArbitraryValue(this.w)) {
      classes.push(`w-${this.w}`);
    }
  }

  private applyFontClasses(classes: string[]): void {
    if (this.fontSize) {
      classes.push(`text-${this.fontSize}`);
    }
    
    if (this.fontWeight) {
      classes.push(`font-${this.fontWeight}`);
    }
  }

  private applyUnderlineClasses(classes: string[]): void {
    if (this.underline) {
      classes.push('underline');
    }
    
    if (this.hoverUnderline) {
      classes.push('hover:underline');
    }
  }

  private applyLegacyClasses(classes: string[]): void {
    if (this.styleClass) {
      classes.push(this.styleClass);
    }
  }

  handleClick(event: MouseEvent) {
    if (this.isButtonDisabled || this.loading) return;

    // Ripple effect
    if (this.buttonElement?.nativeElement) {
      const rect = this.buttonElement.nativeElement.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = event.clientX - rect.left - size / 2;
      const y = event.clientY - rect.top - size / 2;
      this.ripple = { x, y, size, key: Date.now() };
      this.cdr.markForCheck();
      
      setTimeout(() => {
        this.ripple = null;
        this.cdr.markForCheck();
      }, 500);
    }

    // Bounce animation
    this.isAnimating = true;
    this.buttonState = 'pressed';
    this.cdr.markForCheck();
    
    setTimeout(() => {
      this.isAnimating = false;
      this.buttonState = 'released';
      this.cdr.markForCheck();
    }, 200);

    this.onClick.emit(event);
  }

  handleKeyDown(event: KeyboardEvent): void {
    // Handle Enter or Space key to trigger button click (accessibility)
    // Note: Buttons naturally handle Enter key, but we add explicit handling for consistency
    if (event.key === 'Enter' || event.key === ' ') {
      if (this.isButtonDisabled || this.loading) {
        event.preventDefault();
        return;
      }
      // For Space key, prevent default scrolling behavior
      if (event.key === ' ') {
        event.preventDefault();
      }
      // Create a synthetic mouse event to trigger the click handler
      const syntheticEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window
      });
      this.handleClick(syntheticEvent);
    }
  }

  @HostListener('mouseenter') 
  onMouseEnter() {
    this.hoverState = 'hover';
    this.cdr.markForCheck();
  }

  @HostListener('mouseleave') 
  onMouseLeave() {
    this.hoverState = 'noHover';
    this.cdr.markForCheck();
  }

  get isIconOnly(): boolean {
    const hasLabel = this.label || (this.text && !this.isTextColorValue(this.text));
    return !hasLabel && !!(this.leadingIcon || this.trailingIcon);
  }

  handleLeadingIconClick(event: MouseEvent) {
    // Emit the icon-specific event
    this.onLeadingIconClick.emit(event);
    // Also trigger the button's onClick handler
    if (!this.isButtonDisabled && !this.loading) {
      this.handleClick(event);
    }
    // Stop propagation to prevent double-firing from native click bubbling
    event.stopPropagation();
  }

  handleTrailingIconClick(event: MouseEvent) {
    // Emit the icon-specific event
    this.onTrailingIconClick.emit(event);
    // Also trigger the button's onClick handler
    if (!this.isButtonDisabled && !this.loading) {
      this.handleClick(event);
    }
    // Stop propagation to prevent double-firing from native click bubbling
    event.stopPropagation();
  }

  getDisplayText(): string {
    if (this.loading) {
      return this.loadingText ?? this.effectiveLabel ?? '';
    }
    return this.effectiveLabel;
  }

  getDisplayIcons(): { leading: string; trailing: string } {
    if (this.loading) {
      return { leading: '', trailing: '' };
    }
    return {
      leading: this.leadingIcon ?? this.icon,
      trailing: this.trailingIcon
    };
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();
  }

  focus() {
    if (this.buttonElement) {
      this.buttonElement.nativeElement.focus();
    }
  }

  ngAfterViewInit(): void {
    if (this.autofocus && this.buttonElement) {
      setTimeout(() => this.buttonElement.nativeElement.focus(), 0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Mark for check to ensure change detection runs with OnPush strategy
    if (changes['px'] || changes['py'] || changes['p'] || 
        changes['h'] || changes['w'] || changes['bg'] || 
        changes['textColor'] || changes['border'] || changes['borderColor'] ||
        changes['borderB'] || changes['borderT'] || changes['borderL'] || changes['borderR'] ||
        changes['borderBColor'] || changes['borderTColor'] || changes['borderLColor'] || changes['borderRColor'] ||
        changes['rounded'] || changes['fontSize'] || changes['fontWeight']) {
      this.cdr.markForCheck();
    }
  }

  handleFocus(event: FocusEvent) {
    this.onFocus.emit(event);
  }

  handleBlur(event: FocusEvent) {
    this.onBlur.emit(event);
  }

  get badgeColorClass(): string {
    // If batchColur is a hex color, return empty string (handled via inline styles)
    if (this.isHexColor(this.batchColur)) {
      return 'text-white';
    }
    
    switch (this.batchColur) {
      case 'primary': return 'bg-primary-500 text-white';
      case 'success': return 'bg-green-500 text-white';
      case 'info': return 'bg-blue-500 text-white';
      case 'warn': return 'bg-yellow-500 text-white';
      case 'danger': return 'bg-red-500 text-white';
      case 'secondary': return 'bg-gray-500 text-white';
      default: return 'bg-primary-500 text-white';
    }
  }

  get badgeStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};
    
    // If batchColur is a hex color, apply it as background color
    if (this.isHexColor(this.batchColur)) {
      const cleanColor = this.batchColur.startsWith('[') && this.batchColur.endsWith(']') 
        ? this.batchColur.slice(1, -1) 
        : this.batchColur;
      styles['background-color'] = cleanColor;
    }
    
    return styles;
  }

  get computedLoaderColor(): string {
    switch (this.variant) {
      case 'outlined':
      case 'text':
      case 'link':
      case 'plain': {
        const baseColor = this.colors[this.color]?.base;
        if (baseColor) {
          const textColorPattern = /text-[^ ]+/;
          const match = textColorPattern.exec(baseColor);
          return match ? match[0] : 'text-primary-500';
        }
        return 'text-primary-500';
      }
      case 'raised':
      case 'contained':
      default:
        return 'text-white';
    }
  }
}

